<script>
  import Page from '@components/page'
  import Block from '@components/general/block'
  import Button from '@components/buttons/button.svelte'
  import BoxedText from '@components/general/boxed-text.svelte'
  import Table from '@components/table'
  import LoadingArea from '@components/general/loading-area.svelte'
  import { router } from '@inertiajs/svelte'
  import { get } from '@rails/request.js'
  import Dropdown from '@components/general/dropdown.svelte'
  import ConfirmDeleteModal from '@components/general/confirm-delete-modal.svelte'
  import { User, Send, EllipsisVertical, Trash2 } from 'lucide-svelte'
  import { formatDateText, formatDateTime } from '@lib/utils/date'
  import { formatMoney } from '@lib/utils'

  let { member } = $props()

  let confirmed = $derived(member.confirmed)
  let memberships = $derived(member.memberships || [])
  let activeMembership = $derived(memberships.find(m => m.status === 'active'))
  let pendingMembership = $derived(!activeMembership ? memberships.find(m => m.status === 'inactive') : null)
  let displayMembership = $derived(activeMembership || pendingMembership)
  let hasStripe = $derived(!!member.stripe_customer_id)
  let hasActiveMembership = $derived(!!activeMembership)

  let deleteModalOpen = $state(false)
  let deleteProcessing = $state(false)

  function handleDelete() {
    deleteProcessing = true
    router.delete(`/admin/members/${member.id}`, {
      onFinish: () => deleteProcessing = false
    })
  }

  let statusVariant = { active: 'success', inactive: 'default', canceled: 'error' }

  let payments = $state([])
  let paymentsLoading = $state(false)
  let paymentsError = $state(null)

  function resendWelcomeEmail() {
    router.post(`/admin/members/${member.id}/resend_password`)
  }

  let paymentStatusVariant = { succeeded: 'success', processing: 'warning', requires_payment_method: 'error', canceled: 'error' }

  async function fetchPayments() {
    try {
      paymentsLoading = true
      paymentsError = null
      const response = await get(`/api/v1/members/${member.id}/payments`, { responseKind: 'json' })
      const json = await response.json
      if (response.ok) {
        payments = json.data
      } else {
        paymentsError = json.error || 'Failed to load payments'
      }
    } catch (e) {
      paymentsError = e.message
    } finally {
      paymentsLoading = false
    }
  }

  $effect(() => {
    if (hasStripe) fetchPayments()
  })
</script>

<Page.Layout title={member.name}>

  <Page.Navigation title={member.name} Icon={User} links={[
    { text: "Admin", href: "/admin" },
    { text: "Members", href: "/admin/members" }
  ]} />

  <Page.Titles title={member.name} subtitle={member.email_address}>
    {#snippet buttons()}
      <Dropdown buttonProps={{ text: "Actions" }} width="16rem">
        {#snippet contents(item)}
          {@render item({ text: "Resend welcome email", icon: Send, onSelect: resendWelcomeEmail })}
          {@render item({ text: "Delete member", icon: Trash2, onSelect: () => deleteModalOpen = true })}
        {/snippet}
      </Dropdown>
    {/snippet}
  </Page.Titles>

  <Page.Content>

    {#snippet main()}

      <Block.Element label="Account" title="Details">
        <Block.Items>
          <Block.Item label="Email" value={member.email_address} />
          <Block.Item label="Confirmed">
            <BoxedText variant={confirmed ? 'success' : 'error'} size="xs" text={confirmed ? 'Confirmed' : 'Unconfirmed'} />
          </Block.Item>
          <Block.Item label="Joined" value={formatDateText(member.created_at)} />
        </Block.Items>
      </Block.Element>

    {/snippet}

    {#snippet aside()}

      <Block.Element label="Membership" title={activeMembership ? 'Active' : pendingMembership ? 'Pending' : 'No membership'}>
        {#if displayMembership}
          <Block.Items>
            {#if displayMembership.programme}
              <Block.Item label="Programme" value={displayMembership.programme.name} path={`/admin/programmes/${displayMembership.programme.id}`} />
            {/if}
            <Block.Item label="Provider" value={displayMembership.provider} />
            <Block.Item label="Status">
              <BoxedText variant={statusVariant[displayMembership.status]} size="xs" text={pendingMembership ? 'Pending' : 'Active'} />
            </Block.Item>
            <Block.Item label="Valid until" value={formatDateText(displayMembership.entitlement_end_date)} />
          </Block.Items>
        {/if}
      </Block.Element>

    {/snippet}

  </Page.Content>

  {#if hasStripe}
    <Page.Content>
      {#snippet children()}
        <Block.Element label="Stripe" title="Payments">
          {#if paymentsLoading}
            <LoadingArea />
          {:else if paymentsError}
            <p class="text-sm text-red-600">{paymentsError}</p>
          {:else}
            <Block.Table empty={payments.length === 0} emptyMessage="No payments found">
              <Table.Head>
                <Table.Header width="25%">Date</Table.Header>
                <Table.Header width="25%">Amount</Table.Header>
                <Table.Header width="25%">Description</Table.Header>
                <Table.Header width="25%" align="center">Status</Table.Header>
              </Table.Head>
              <Table.Body>
                {#each payments as payment (payment.id)}
                  <Table.Row>
                    <Table.Cell width="25%">{formatDateTime(payment.created)}</Table.Cell>
                    <Table.Cell width="25%">{formatMoney(payment.amount)}</Table.Cell>
                    <Table.Cell width="25%">{payment.description || '—'}</Table.Cell>
                    <Table.Cell width="25%" align="center">
                      <BoxedText variant={paymentStatusVariant[payment.status] || 'default'} size="xs" text={payment.status} />
                    </Table.Cell>
                  </Table.Row>
                {/each}
              </Table.Body>
            </Block.Table>
          {/if}
        </Block.Element>
      {/snippet}
    </Page.Content>
  {/if}

</Page.Layout>

<ConfirmDeleteModal
  bind:open={deleteModalOpen}
  title="Delete member"
  message={hasActiveMembership
    ? `${member.name} has an active membership. Deleting this account will cancel their membership, remove all associated data, and revoke their access immediately.`
    : `This will permanently delete ${member.name}'s account and all associated data.`
  }
  warningMessage="This action cannot be undone."
  confirmValue={hasActiveMembership ? member.email_address : null}
  confirmLabel="Type"
  buttonText="Delete member"
  processing={deleteProcessing}
  onConfirm={handleDelete}
/>
